import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import { Button, Card, Chip, Spinner } from '@structural-climbing/ui'
import { PageHeader } from '../../components/PageHeader'
import { PlanFormModal, type PlanFormData } from './PlanFormModal'
import type { GymPlan } from './types'

interface PlansSectionProps {
  plans: GymPlan[]
  loading: boolean
  onRefresh: () => void
}

export function PlansSection({ plans, loading, onRefresh }: PlansSectionProps) {
  const { getAccessTokenSilently } = useAuth()
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const PLAN_TYPE_LABELS: Record<GymPlan['plan_type'], string> = {
    day_pass: t('plans.typeDayPass'),
    entry_based: t('plans.typeEntryBased'),
    duration_based: t('plans.typeDurationBased'),
  }

  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<GymPlan | undefined>(undefined)
  const [pendingId, setPendingId] = useState<string | null>(null)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  useEffect(() => {
    if (!modalOpen) setEditTarget(undefined)
  }, [modalOpen])

  async function handleSave(data: PlanFormData) {
    const token = await getAccessTokenSilently()
    const url = editTarget ? `${apiUrl}/gym-plans/${editTarget.id}` : `${apiUrl}/gym-plans`
    const method = editTarget ? 'PATCH' : 'POST'

    // The instructor flag goes only when it changed. Every other save then
    // sends exactly what it did before classes existed.
    // The partner fields likewise go only for a partner plan, or when an
    // existing one stops being one.
    const { includes_instructor, external_provider, external_visit_price, ...rest } = data
    const body: Record<string, unknown> =
      includes_instructor === (editTarget?.includes_instructor ?? false)
        ? { ...rest }
        : { ...rest, includes_instructor }
    if (external_provider || editTarget?.external_provider) {
      body.external_provider = external_provider
      body.external_visit_price = external_visit_price
    }

    const res = await fetch(url, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
    const json = (await res.json()) as { error?: string }
    if (!res.ok) throw new Error(json.error ?? t('plans.errorSave'))
    onRefresh()
  }

  async function handleToggle(plan: GymPlan) {
    setPendingId(plan.id)
    try {
      const token = await getAccessTokenSilently()
      await fetch(`${apiUrl}/gym-plans/${plan.id}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !plan.is_active }),
      })
      onRefresh()
    } finally {
      setPendingId(null)
    }
  }

  async function handleDelete(plan: GymPlan) {
    if (!window.confirm(t('plans.deleteConfirm', { name: plan.name }))) return
    setDeleteError(null)
    setPendingId(plan.id)
    try {
      const token = await getAccessTokenSilently()
      const res = await fetch(`${apiUrl}/gym-plans/${plan.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      const json = (await res.json()) as { error?: string }
      if (!res.ok) {
        setDeleteError(json.error ?? t('plans.errorDelete'))
        return
      }
      onRefresh()
    } finally {
      setPendingId(null)
    }
  }

  return (
    <>
      <div>
        <PageHeader
          title={t('plans.title')}
          subtitle={t('plans.sectionSubtitle')}
          primaryAction={{
            label: t('plans.newPlan'),
            onPress: () => {
              setEditTarget(undefined)
              setModalOpen(true)
            },
          }}
        />
        <div>
          {deleteError && <p className="mb-3 text-sm text-danger">{deleteError}</p>}

          {loading ? (
            <div className="flex justify-center py-6">
              <Spinner size="sm" color="primary" />
            </div>
          ) : plans.length === 0 ? (
            <p className="py-6 text-center text-sm text-foreground/40">{t('plans.empty')}</p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {plans.map((plan) => (
                <Card key={plan.id} shadow="sm" className="flex flex-col gap-2 p-4">
                  <div className="flex items-start gap-2">
                    <p className="min-w-0 flex-1 text-base font-semibold">{plan.name}</p>
                    <Chip
                      size="sm"
                      variant="flat"
                      color={plan.is_active ? 'success' : 'default'}
                      className="shrink-0"
                    >
                      {plan.is_active ? t('common.active') : t('common.inactive')}
                    </Chip>
                  </div>
                  <p className="text-2xl font-bold tabular-nums tracking-tight">
                    {plan.external_provider
                      ? t('plans.externalPerVisit', {
                          price: Number(plan.external_visit_price ?? 0).toFixed(2),
                          currency: plan.currency,
                        })
                      : `${plan.price.toFixed(2)} ${plan.currency}`}
                  </p>
                  <p className="text-sm text-foreground/55">
                    {PLAN_TYPE_LABELS[plan.plan_type]}
                    {plan.plan_type === 'entry_based' && plan.max_entries
                      ? ` · ${t('plans.cardEntries', { count: plan.max_entries })}`
                      : ''}
                    {plan.plan_type !== 'day_pass'
                      ? ` · ${t('plans.cardDays', { count: plan.duration_days })}`
                      : ''}
                  </p>
                  {(plan.promo_tag ||
                    plan.payable_online ||
                    plan.is_addon ||
                    plan.includes_instructor ||
                    plan.external_provider) && (
                    <div className="flex flex-wrap gap-1.5">
                      {plan.promo_tag && (
                        <Chip size="sm" variant="flat" color="warning">
                          {plan.promo_tag}
                        </Chip>
                      )}
                      {plan.payable_online && (
                        <Chip size="sm" variant="flat" color="primary">
                          {t('plans.payableOnlineChip')}
                        </Chip>
                      )}
                      {plan.is_addon && (
                        <Chip size="sm" variant="flat" color="secondary">
                          {t('plans.addonChip')}
                        </Chip>
                      )}
                      {plan.includes_instructor && (
                        <Chip size="sm" variant="flat" color="success">
                          {t('plans.instructorChip')}
                        </Chip>
                      )}
                      {plan.external_provider && (
                        <Chip size="sm" variant="flat" color="warning">
                          {t('plans.externalChip', { provider: plan.external_provider })}
                        </Chip>
                      )}
                    </div>
                  )}
                  <div className="mt-auto flex flex-wrap items-center gap-2 pt-2">
                    <Button
                      size="sm"
                      variant="bordered"
                      className="border-1"
                      isLoading={pendingId === plan.id}
                      isDisabled={pendingId !== null}
                      onClick={() => {
                        setEditTarget(plan)
                        setModalOpen(true)
                      }}
                    >
                      {t('common.edit')}
                    </Button>
                    <Button
                      size="sm"
                      variant="flat"
                      isLoading={pendingId === plan.id}
                      isDisabled={pendingId !== null}
                      onClick={() => void handleToggle(plan)}
                    >
                      {plan.is_active ? t('common.deactivate') : t('common.activate')}
                    </Button>
                    <Button
                      size="sm"
                      variant="light"
                      color="danger"
                      isLoading={pendingId === plan.id}
                      isDisabled={pendingId !== null}
                      onClick={() => void handleDelete(plan)}
                    >
                      {t('common.delete')}
                    </Button>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>

      <PlanFormModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onSave={handleSave}
        initialValues={editTarget}
      />
    </>
  )
}
